import { z } from "zod";

import { date, reference, text, time } from "@frontend/lib/form/validators";
import { msg } from "@lingui/core/macro";

export const hourSchema = z
  .object({
    id: z.number().int().nullable().default(null),
    begin: time.default(null),
    end: time.default(null),
    statusId: reference.default(null),
  })
  .superRefine(({ begin, end }, ctx) => {
    // A range may be left empty, as in the legacy form, but never inverted.
    // Equal times (e.g. a stray "00:00"–"00:00" from the legacy form) are not
    // inverted, so they are left alone rather than rejected.
    if (begin && end && begin > end) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`The end comes before the start`),
        path: ["end"],
      });
    }
  });

export const recurrenceExceptionSchema = z
  .object({
    id: z.number().int().nullable().default(null),
    type: z
      .enum(["holidays", "yearlydates", "dates", "weeks"])
      .default("dates"),
    start: date.default(null),
    stop: date.default(null),
    nbWeeks: z.number().int().positive().nullable().default(null),
  })
  .superRefine(({ type, start, stop, nbWeeks }, ctx) => {
    if ("weeks" === type && null === nbWeeks) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`Enter a number of weeks`),
        path: ["nbWeeks"],
      });
    }

    if ("weeks" !== type && null === start) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`Enter a date`),
        path: ["start"],
      });
    }

    if (start && stop && start > stop) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`The end comes before the start`),
        path: ["stop"],
      });
    }
  });

export const recurrenceSchema = z.object({
  rule: z.string().default(""),
  exceptions: z.array(recurrenceExceptionSchema).default([]),
});

export const taskSchema = z
  .object({
    id: z.number().int().nullable().default(null),
    serviceGroupId: reference.default(null),
    typeId: reference.default(null),
    workCategoryId: reference.default(null),
    dayLagId: reference.default(null),
    timeLagId: reference.default(null),
    hours: z.array(hourSchema).default([]),
    recurrence: recurrenceSchema,
    deletable: z.boolean().default(true),
  })
  .superRefine(({ typeId, workCategoryId }, ctx) => {
    // The two the legacy form marks required.
    if (null === typeId) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`Choose a task type`),
        path: ["typeId"],
      });
    }

    if (null === workCategoryId) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`Choose a work category`),
        path: ["workCategoryId"],
      });
    }
  });

export const jobSchema = z
  .object({
    lockVersion: z.string().nullable().default(null),
    workerId: reference.default(null),
    functionId: reference.default(null),
    categoryId: reference.default(null),
    holidayActionId: reference.default(null),
    billingId: reference.default(null),
    refundLunch: z.boolean().default(false),
    refundRide: z.boolean().default(false),
    inContract: z.boolean().default(false),
    startDate: date.default(null),
    stopDate: date.default(null),
    tasks: z.array(taskSchema).default([]),
  })
  .superRefine(({ startDate, stopDate }, ctx) => {
    if (startDate && stopDate && startDate > stopDate) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`The end comes before the start`),
        path: ["stopDate"],
      });
    }
  });

export const duplicateJobSchema = z.object({
  startDate: date.default(null),
  workerId: reference.default(null),
});

export type HourValues = z.infer<typeof hourSchema>;
export type RecurrenceExceptionValues = z.infer<
  typeof recurrenceExceptionSchema
>;
export type RecurrenceValues = z.infer<typeof recurrenceSchema>;
export type TaskValues = z.infer<typeof taskSchema>;
export type JobValues = z.infer<typeof jobSchema>;
export type DuplicateJobValues = z.infer<typeof duplicateJobSchema>;
